// item 139 — pull the REAL, human-readable reason out of a failed API call so the UI
// can show it instead of a scary generic "Save failed." The backend returns business
// errors as { errors: ["..."] } (GenericBizRunner / ActionResultHelper), sometimes a
// plain string, sometimes ASP.NET ModelState { errors: { field: ["..."] } }.
// item 1499 — ALSO the { error: "…" } shape: the API's ExceptionHandlingMiddleware writes
// exactly that for every entity-guard 409/400 ("Editing this invoice is forbidden due to
// its status."), which every caller was dropping on the floor as "Something went wrong".
export const apiErrorMessage = (e: any, fallback = 'Something went wrong. Please try again.'): string => {
  const data = e?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  const errs = data?.errors;
  if (Array.isArray(errs) && errs.length) return String(errs[0]);
  if (errs && typeof errs === 'object') {
    const first = Object.values(errs)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  // item 1499 — ExceptionHandlingMiddleware: { error: "<exception message>" }
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data?.title === 'string') return data.title;
  return e?.message || fallback;
};

// item 1499 — the PORTAL variant: customers (and Outside Sales reps) never see developer
// text. Returns the parsed message only when it reads like a sentence a human wrote for a
// human — no LINQ/CLR noise ("Sequence contains…", "Object reference…"), no stack-ish text
// ("   at Foo.Bar()", "Exception:"), no axios/network boilerplate, nothing over ~200 chars —
// otherwise the caller's fallback.
const DEV_NOISE = [
  /sequence contains/i,
  /object reference/i,
  /nullreference/i,
  /exception\b/i, // CLR type names end in it mid-word (InvalidOperationException: …)
  /\bat [\w.<>`$]+\(/, // "   at basix.bl.Foo.Bar(…)" stack frames
  /\bstack ?trace\b/i,
  /^request failed with status code/i,
  /^network error$/i,
  /^timeout of \d+ms exceeded/i,
  /^an unexpected error occurred\.?$/i, // the middleware's 500 placeholder — says nothing useful
  /<\/?[a-z][^>]*>/i, // an HTML error page
  /\{[\s\S]*:[\s\S]*\}/, // serialized JSON leaking through
  /\n/, // multi-line = stack-ish
];
const MAX_PORTAL_MESSAGE = 200;

export const portalErrorMessage = (e: any, fallback: string): string => {
  const msg = apiErrorMessage(e, '');
  if (!msg) return fallback;
  if (msg.length > MAX_PORTAL_MESSAGE) return fallback;
  if (DEV_NOISE.some((re) => re.test(msg))) return fallback;
  return msg;
};

export default apiErrorMessage;
